import { SortDirection } from 'constants/SortDirection';
import { makeObservable, override, runInAction } from 'mobx';
import { destroy, EntityName, getDetailsForCorrectPagination, post } from 'stores/_services/dataService';
import { AccessTokenStore } from 'stores/accessToken/AccessTokenStore';
import BaseAccessTokenListStore from 'stores/accessToken/BaseAccessTokenListStore';
import { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';

export default class AccessTokenListStore extends BaseAccessTokenListStore<AccessTokenStore> {
    constructor() {
        super({
            sortBy: 'created',
            sortDirection: SortDirection.DESC,
            itemStore: AccessTokenStore
        });

        makeObservable(this, {
            getAccessTokens: override,
            deleteToken: override
        });
    }

    public async getAccessTokens(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const payload = {
            search: args.filter?.search || null,
            limit: pageSize,
            skip: (pageNumber - 1) * pageSize
        };

        const { data } = await post(`${EntityName.PERSONAL_ACCESS_TOKENS}/filterByUserEmail`, payload);
        const { data: totalCount } = await post(`${EntityName.PERSONAL_ACCESS_TOKENS}/countFilteredByUserEmail`, { search: args.filter?.search || null });

        this._updateData(data);
        this._updateIds(data, 0);

        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount.length;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getAccessTokensForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const params = {
            filter: filter || this.filter,
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            include: {
                relation: 'user',
                scope: {
                    fields: ['email']
                }
            }
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<AccessTokenStore>({
            params,
            entityName: EntityName.PERSONAL_ACCESS_TOKENS
        });

        this.updateStateForCorrectPagination<AccessTokenStore>({ args: { ...args, reset: true }, data, totalCount });
    }

    public async deleteToken(id: string): Promise<void> {
        await destroy(`${EntityName.PERSONAL_ACCESS_TOKENS}/${id}`);

        this.clearItem(id);
    }
}
